import { useCallback, useEffect, useState } from 'react';
import apiService from '../services/api';
import FirstRewardImagesField, { FIRST_REWARD_MAX_IMAGES } from './FirstRewardImagesField';

const REWARD_TYPE_OPTIONS = [
  { value: 'experience', label: 'Experience' },
  { value: 'merchandise', label: 'Merchandise' },
  { value: 'apparel', label: 'Apparel' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'gift_card', label: 'Gift Card' },
];

const REWARD_TYPE_LABELS = REWARD_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const label = {
  display: 'block',
  fontFamily: '"Baikal Book"',
  fontSize: '12px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '6px',
};

const input = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  color: '#111827',
  boxSizing: 'border-box',
};

const ghostButton = {
  padding: '6px 12px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  backgroundColor: 'white',
  fontFamily: '"Baikal Book"',
  fontSize: '13px',
  color: '#374151',
  cursor: 'pointer',
};

const menuItem = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  backgroundColor: 'transparent',
  color: '#374151',
  border: 'none',
  fontFamily: '"Baikal Book"',
  fontSize: '14px',
  cursor: 'pointer',
  textAlign: 'left',
};

const emptyRung = () => ({
  key: `new-${Math.random().toString(36).slice(2)}`,
  id: null,
  images: [],
  title: '',
  description: '',
  whats_included: '',
  points_cost: '',
  reward_type: 'experience',
  confirmation_image_url: '',
  location: '',
  is_active: true,
});

const fromApi = (rung) => ({
  key: rung.id,
  id: rung.id,
  images: Array.isArray(rung.images) ? rung.images : [],
  title: rung.title || '',
  description: rung.description || '',
  whats_included: rung.whats_included || '',
  points_cost: rung.points_cost ?? '',
  reward_type: rung.reward_type || 'experience',
  confirmation_image_url: rung.confirmation_image_url || '',
  location: rung.location || '',
  is_active: rung.is_active !== false,
});

const toPayload = (rung) => ({
  ...(rung.id ? { id: rung.id } : {}),
  images: rung.images,
  title: rung.title.trim(),
  description: rung.description,
  whats_included: rung.whats_included,
  points_cost: Number(rung.points_cost),
  reward_type: rung.reward_type,
  confirmation_image_url: rung.confirmation_image_url,
  location: rung.location,
  is_active: rung.is_active,
});

/**
 * Runs over the whole list before every write, since the API replaces the list
 * wholesale and a single bad rung would be stored alongside the good ones.
 *
 * Order is whatever the admin dragged it to — the server stores each rung's
 * position from its index in the submitted list — so costs are checked
 * individually and not against each other.
 *
 * @returns {string} an error message, or '' when the list is valid
 */
function validateLadder(rungs) {
  for (let i = 0; i < rungs.length; i += 1) {
    const rung = rungs[i];
    if (!rung.title.trim()) return `Reward ${i + 1} needs a title.`;
    const cost = Number(rung.points_cost);
    if (!Number.isFinite(cost) || cost <= 0) {
      return `Reward ${i + 1} needs a points cost above zero.`;
    }
  }
  return '';
}

const FirstRewardsSection = ({ schoolId, schoolName }) => {
  const [rungs, setRungs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  // The rung currently open in the editor. null when the modal is closed.
  const [draft, setDraft] = useState(null);
  // Reordering: `dragKey` is the card being carried, `overIndex` the slot it
  // would land in. The whole card is the drag target.
  const [dragKey, setDragKey] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const data = await apiService.getSchoolFirstRewards(schoolId);
        if (cancelled) return;
        setRungs((Array.isArray(data) ? data : []).map(fromApi));
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.detail ||
              err.message ||
              'Failed to load first rewards.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!event.target.closest('.fr-dropdown-menu')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  /**
   * Writes the whole ladder and adopts whatever the server returns.
   *
   * Every card action goes through here: the API replaces the list in one PUT
   * rather than exposing per-rung routes, so "delete this rung" is really "save
   * the list without it". That also means each action persists immediately —
   * there is no separate save step to forget.
   *
   * @returns {Promise<boolean>} whether the write succeeded
   */
  const persist = useCallback(
    async (nextRungs, successMessage) => {
      const problem = validateLadder(nextRungs);
      if (problem) {
        setError(problem);
        return false;
      }

      setSaving(true);
      setError('');
      try {
        const saved = await apiService.putSchoolFirstRewards(
          schoolId,
          nextRungs.map(toPayload)
        );
        setRungs((Array.isArray(saved) ? saved : []).map(fromApi));
        setSuccess(successMessage);
        return true;
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.detail ||
            err.message ||
            'Failed to save first rewards.'
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [schoolId]
  );

  const patchDraft = useCallback((changes) => {
    setDraft((prev) => (prev ? { ...prev, ...changes } : prev));
  }, []);

  /**
   * Patch derived from the draft's own current value. Uploads land out of order
   * and after re-renders, so appending must read the latest state rather than a
   * value captured when the handler was created.
   */
  const patchDraftFrom = useCallback((compute) => {
    setDraft((prev) => (prev ? { ...prev, ...compute(prev) } : prev));
  }, []);

  const resetDrag = useCallback(() => {
    setDragKey(null);
    setOverIndex(null);
  }, []);

  const dropAt = useCallback(
    (targetIndex) => {
      const fromIndex = rungs.findIndex((r) => r.key === dragKey);
      resetDrag();
      if (fromIndex < 0 || fromIndex === targetIndex) return;
      const next = rungs.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(targetIndex, 0, moved);
      persist(next, 'Order updated.');
    },
    [rungs, dragKey, persist, resetDrag]
  );

  const toggleActive = useCallback(
    (rung) => {
      setActiveDropdown(null);
      const next = rungs.map((r) =>
        r.key === rung.key ? { ...r, is_active: !r.is_active } : r
      );
      persist(next, rung.is_active ? 'Reward deactivated.' : 'Reward activated.');
    },
    [rungs, persist]
  );

  const deleteRung = useCallback(
    (rung) => {
      setActiveDropdown(null);
      if (!window.confirm(`Delete "${rung.title || 'this reward'}"?`)) return;
      persist(
        rungs.filter((r) => r.key !== rung.key),
        'Reward deleted.'
      );
    },
    [rungs, persist]
  );

  /** Uploads one file and returns its S3 URL, or '' if the upload failed. */
  const uploadOne = useCallback(
    async (file) => {
      setError('');
      try {
        const data = await apiService.uploadFirstRewardImage(schoolId, file);
        return data.image_url || '';
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.detail ||
            err.message ||
            'Failed to upload image.'
        );
        return '';
      }
    },
    [schoolId]
  );

  const handleDraftSave = async () => {
    if (!draft) return;
    const exists = rungs.some((r) => r.key === draft.key);
    const next = exists
      ? rungs.map((r) => (r.key === draft.key ? draft : r))
      : rungs.concat([draft]);
    const saved = await persist(next, exists ? 'Reward updated.' : 'Reward added.');
    if (saved) setDraft(null);
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px' }}>
        <p style={{ fontFamily: '"Baikal Book"', fontSize: '14px', color: '#6b7280' }}>
          Loading first rewards…
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
      }}
    >
      <h3
        style={{
          fontFamily: '"Baikal Book"',
          fontSize: '20px',
          fontWeight: 600,
          color: '#111827',
          marginBottom: '8px',
        }}
      >
        First rewards — {schoolName}
      </h3>
      <p
        style={{
          fontFamily: '"Baikal Book"',
          fontSize: '13px',
          color: '#6b7280',
          marginBottom: '24px',
          lineHeight: 1.5,
        }}
      >
        The reward ladder on the app home screen. Each rung unlocks when the student&rsquo;s
        lifetime points reach its cost. Drag the cards by their grip to set the order shown
        in the app — it is saved as soon as you drop.
      </p>

      {error && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 14px',
            borderRadius: '8px',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontFamily: '"Baikal Book"',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      {/* Floats over the page rather than sitting inline: actions fire from a
          menu partway down a long list, so an inline banner at the top goes unseen. */}
      {success && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 20px',
            borderRadius: '8px',
            backgroundColor: '#10B981',
            color: 'white',
            fontFamily: '"Baikal Book"',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'slideIn 0.3s ease-out',
          }}
        >
          {success}
          <button
            type="button"
            onClick={() => setSuccess('')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
          <style>{`
            @keyframes slideIn {
              from { transform: translateX(100%); opacity: 0; }
              to { transform: translateX(0); opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {rungs.length === 0 && (
        <p
          style={{
            fontFamily: '"Baikal Book"',
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '20px',
          }}
        >
          No first rewards yet. The home screen shows the points total without a ladder until
          you add one.
        </p>
      )}

      {rungs.map((rung, index) => (
        <div
          key={rung.key}
          draggable
          onDragStart={(e) => {
            setDragKey(rung.key);
            e.dataTransfer.effectAllowed = 'move';
            // Firefox ignores a drag that carries no payload.
            e.dataTransfer.setData('text/plain', rung.key);
          }}
          onDragEnd={resetDrag}
          onDragOver={(e) => {
            if (!dragKey) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (overIndex !== index) setOverIndex(index);
          }}
          onDrop={(e) => {
            e.preventDefault();
            dropAt(index);
          }}
          style={{
            position: 'relative',
            border:
              dragKey && overIndex === index && dragKey !== rung.key
                ? '2px solid #00C8FF'
                : '2px solid #d1d5db',
            borderRadius: '10px',
            padding: '22px 24px',
            marginBottom: '16px',
            backgroundColor: rung.is_active ? 'white' : '#fafafa',
            opacity: dragKey === rung.key ? 0.5 : 1,
            cursor: dragKey === rung.key ? 'grabbing' : 'grab',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {/* Affordance only — the whole card is the drag target. */}
            <span
              aria-hidden="true"
              title="Drag to reorder"
              style={{
                flexShrink: 0,
                marginTop: '1px',
                fontFamily: '"Baikal Book"',
                fontSize: '15px',
                color: '#9ca3af',
                userSelect: 'none',
                lineHeight: 1.3,
              }}
            >
              ⠿
            </span>
            <span
              style={{
                flexShrink: 0,
                marginTop: '2px',
                fontFamily: '"Baikal Book"',
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
              }}
            >
              {index + 1}.
            </span>

            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '4px',
                }}
              >
                <span
                  style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '17px',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {rung.title || 'Untitled reward'}
                </span>
                {!rung.is_active && (
                  <span
                    style={{
                      padding: '1px 7px',
                      borderRadius: '100px',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      fontFamily: '"Baikal Book"',
                      fontSize: '11px',
                      fontWeight: 600,
                    }}
                  >
                    Inactive
                  </span>
                )}
              </div>
              <div
                style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  color: '#6b7280',
                }}
              >
                {Number(rung.points_cost || 0).toLocaleString()} pts ·{' '}
                {REWARD_TYPE_LABELS[rung.reward_type] || rung.reward_type}
              </div>
            </div>

            <div className="fr-dropdown-menu" style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() =>
                setActiveDropdown(activeDropdown === rung.key ? null : rung.key)
              }
              style={{
                padding: '4px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: 'none',
                borderRadius: '4px',
                fontFamily: '"Baikal Book"',
                fontSize: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
                width: '32px',
                height: '32px',
              }}
              title="Reward options"
            >
              ⋮
            </button>
            {activeDropdown === rung.key && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  minWidth: '150px',
                }}
              >
                <button
                  type="button"
                  style={menuItem}
                  onClick={() => {
                    setActiveDropdown(null);
                    setError('');
                    setDraft(rung);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  style={{ ...menuItem, borderTop: '1px solid #e5e7eb' }}
                  onClick={() => toggleActive(rung)}
                >
                  {rung.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  type="button"
                  style={{
                    ...menuItem,
                    borderTop: '1px solid #e5e7eb',
                    color: '#dc2626',
                  }}
                  onClick={() => deleteRung(rung)}
                >
                  Delete
                </button>
                </div>
              )}
            </div>
          </div>

          {rung.images.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                marginTop: '18px',
              }}
            >
              {rung.images.map((url, imageIndex) => (
                <img
                  key={`${url}-${imageIndex}`}
                  src={url}
                  alt=""
                  title={imageIndex === 0 ? 'Shown first in the app' : `Image ${imageIndex + 1}`}
                  style={{
                    width: '104px',
                    height: '104px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                  }}
                />
              ))}
            </div>
          )}

          {/* The confirmation image is still editable in the modal — it is just
              left out of the preview to keep the card about the reward itself. */}
          <div style={{ marginTop: '18px' }}>
            {[
              ['Location', rung.location],
              ['Description', rung.description],
              ["What's included", rung.whats_included],
            ].map(([fieldLabel, value]) => (
              <div
                key={fieldLabel}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr',
                  gap: '14px',
                  padding: '10px 0',
                  borderTop: '1px solid #f3f4f6',
                  alignItems: 'start',
                }}
              >
                <span
                  style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6b7280',
                    lineHeight: 1.6,
                  }}
                >
                  {fieldLabel}
                </span>
                <span
                  style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '14px',
                    lineHeight: 1.6,
                    color: value ? '#374151' : '#9ca3af',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {value || '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
        <button
          type="button"
          style={ghostButton}
          disabled={saving}
          onClick={() => {
            setError('');
            setDraft(emptyRung());
          }}
        >
          + Add reward
        </button>
        {saving && (
          <span
            style={{
              alignSelf: 'center',
              fontFamily: '"Baikal Book"',
              fontSize: '13px',
              color: '#6b7280',
            }}
          >
            Saving…
          </span>
        )}
      </div>

      {draft && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setDraft(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '28px',
              width: '640px',
              maxWidth: '92%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                fontFamily: '"Baikal Book"',
                fontSize: '18px',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '20px',
              }}
            >
              {rungs.some((r) => r.key === draft.key) ? 'Edit reward' : 'Add reward'}
            </h3>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '16px',
                marginBottom: '16px',
              }}
            >
              <div>
                <label style={label}>Title</label>
                <input
                  type="text"
                  value={draft.title}
                  onChange={(e) => patchDraft({ title: e.target.value })}
                  placeholder="Athletics Facility Tour"
                  style={input}
                />
              </div>
              <div>
                <label style={label}>Points cost</label>
                <input
                  type="number"
                  min="1"
                  value={draft.points_cost}
                  onChange={(e) => patchDraft({ points_cost: e.target.value })}
                  placeholder="1500"
                  style={input}
                />
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px',
              }}
            >
              <div>
                <label style={label}>Location</label>
                <input
                  type="text"
                  value={draft.location}
                  onChange={(e) => patchDraft({ location: e.target.value })}
                  placeholder="Gate C, Memorial Stadium"
                  style={input}
                />
              </div>
              <div>
                <label style={label}>Reward type</label>
                <select
                  value={draft.reward_type}
                  onChange={(e) => patchDraft({ reward_type: e.target.value })}
                  style={input}
                >
                  {REWARD_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={label}>Description</label>
              <textarea
                value={draft.description}
                onChange={(e) => patchDraft({ description: e.target.value })}
                rows={2}
                placeholder="Short tagline shown under the title"
                style={{ ...input, resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={label}>What&rsquo;s Included</label>
              <textarea
                value={draft.whats_included}
                onChange={(e) => patchDraft({ whats_included: e.target.value })}
                rows={2}
                placeholder="60-minute guided tour, access to selected training areas."
                style={{ ...input, resize: 'vertical' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={label}>Images</label>
              <FirstRewardImagesField
                urls={draft.images}
                inputId={`first-reward-images-${draft.key}`}
                onAppend={(added) =>
                  patchDraftFrom((current) => ({
                    images: current.images.concat(added).slice(0, FIRST_REWARD_MAX_IMAGES),
                  }))
                }
                onRemove={(index) =>
                  patchDraftFrom((current) => ({
                    images: current.images.filter((_, i) => i !== index),
                  }))
                }
                onUpload={uploadOne}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={label}>Confirmation image</label>
              <FirstRewardImagesField
                urls={draft.confirmation_image_url ? [draft.confirmation_image_url] : []}
                inputId={`first-reward-confirmation-${draft.key}`}
                maxImages={1}
                onAppend={(added) =>
                  patchDraft({ confirmation_image_url: added[0] || '' })
                }
                onRemove={() => patchDraft({ confirmation_image_url: '' })}
                onUpload={uploadOne}
              />
            </div>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: '"Baikal Book"',
                fontSize: '13px',
                color: '#374151',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => patchDraft({ is_active: e.target.checked })}
              />
              Active — show this reward in the app
            </label>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '24px',
              }}
            >
              <button type="button" style={ghostButton} onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDraftSave}
                disabled={saving}
                style={{
                  padding: '10px 22px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: saving ? '#9ca3af' : '#111827',
                  color: 'white',
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: saving ? 'default' : 'pointer',
                }}
              >
                {saving ? 'Saving…' : 'Save reward'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirstRewardsSection;
